import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import useSelectedPatient from "@/hooks/local-storage/use-selected-patient";
import { useSnackbar } from "@/hooks/useSnackbar";
import { useCommunicationMessages } from "@/queries/patient/use-patient-get-all-sms";
import { useSendSmsToPatient } from "@/queries/patient/use-patient-send-sms-to-patient";
import useFcmSmsEventListener from "@/services/fcm/listeners/FcmSmsEventListener";
import { getErrorSnackbar, getXtenantId } from "@/utils/utils";
import { MessageContainer } from "@/pages/app/provider/communications/messages/MessageContainer";

const PatientChat = () => {
  const [selectedPatient] = useSelectedPatient();
  const queryClient = useQueryClient();
  const { mutateAsync: sendMessageToPatient } = useSendSmsToPatient();
  const {
    data: smsRecords,
    refetch,
    dataUpdatedAt,
  } = useCommunicationMessages({
    pagination: { page: 0, pageSize: 200 },
    xTENANTID: getXtenantId(),
    patientUuid: selectedPatient?.uuid,
  });

  // Loading the thread marks it read on the server, so the unread badge is re-read after each load.
  useEffect(() => {
    if (!dataUpdatedAt) return;

    queryClient.invalidateQueries({ queryKey: ["fcm-sms-count-get"] });
  }, [dataUpdatedAt, queryClient]);

  /** listen to firebase sms notifications */
  useFcmSmsEventListener({
    onSmsNotification: () => refetch(),
  });
  const showSnackbar = useSnackbar();
  const onSend = async (msg: string, user: any) => {
    if (msg?.trim()) {
      await sendMessageToPatient({
        emailToPatient: {
          description: msg,
          patientUuids: [user.uuid],
        },
        xTENANTID: getXtenantId(),
      }).catch((error) => {
        showSnackbar(getErrorSnackbar(error));
      });

      return await refetch();
    }

    return new Promise(() => {});
  };

  return (
    <MessageContainer
      hideTopBar
      user={selectedPatient}
      onSend={onSend}
      messages={smsRecords?.data}
    />
  );
};

export default PatientChat;
